import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useGlobalConfig, useBase } from '@airtable/blocks/interface/ui';
import { railViewsKey, getBlockIdBestEffort, migrateKeyKeepLegacy } from './dock/dockViews';
import { LENS_GROUPS, LENSES, LensId, lensLabel, lensPreview, type LensDef } from '../data/lenses';
import Filters, { FilterState, emptyFilters, filterCount, serFilters, deserFilters } from '../filters/Filters';
import type { Parcel } from '../data/useParcels';
import { searchHaystack } from '../data/searchHaystack';
export { searchHaystack };
import { fmt } from '../data/aggregate';
import { T } from '../theme/tokens';
import { SplitPicker } from '../ui/SplitPicker';

// Saved-view ser/deser moved to filters/Filters (serFilters/deserFilters) — STRUCTURAL over
// FilterState, so adding a filter group can no longer silently fall out of saved views (the
// hand-kept SETKEYS list here had already missed `permitIssued`).

// Per-user fallback store. GlobalConfig is read-only in the published-interface runtime (only base
// editors in the builder can set it), so personal views live in localStorage and always save.
// The key is NAMESPACED per base + block installation (like the dock's persistence) so two bases
// sharing an origin can never read each other's personal views; the legacy un-namespaced
// 'lfeSavedViews.v1' is copied in once and kept as a backup (migrateKeyKeepLegacy). Shared views
// live in globalConfig (already per base) and are not touched by this.
const LEGACY_LS_KEY = 'lfeSavedViews.v1';
const lsRead = (key: string): any[] => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } };
const lsWrite = (key: string, v: any[]) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode */ } };
// Default-view pointer: the NAME of the saved view to auto-apply on load, so the map opens already
// in that lens + filters + splits. A SHARED default (set by an editor, stored in globalConfig under
// DEFAULT_GC_KEY) is the base-wide default everyone sees; a PERSONAL default (any viewer, stored in
// localStorage) overrides it for just that browser. We key personal off the same namespaced views
// key so it can't bleed across bases sharing an origin.
const DEFAULT_GC_KEY = 'lfeDefaultView';
const lsReadStr = (key: string): string => { try { return localStorage.getItem(key) || ''; } catch { return ''; } };
const lsWriteStr = (key: string, v: string) => { try { v ? localStorage.setItem(key, v) : localStorage.removeItem(key); } catch { /* private mode */ } };

// searchHaystack moved to ../data/searchHaystack (pure module) so the test harness can
// import the REAL function without dragging in @airtable/blocks. Re-exported below.

/** Per-row palette preview for the Color-by picker: a tiny chip showing the colours that lens will
 *  paint the map with — a gradient bar for sequential lenses, hard-edged blocks for categorical ones
 *  — so the list reads as a legend. `on` (selected row, dark band) lightens the chip border. */
function LensSwatch({ lens, on }: { lens: LensDef; on: boolean }) {
  const { kind, colors } = lensPreview(lens);
  const common: React.CSSProperties = { width: 22, height: 12, borderRadius: 3, flex: '0 0 auto',
    overflow: 'hidden', border: `1px solid ${on ? 'rgba(255,255,255,.45)' : 'rgba(0,0,0,.14)'}` };
  if (kind === 'gradient')
    return <span aria-hidden style={{ ...common, background: `linear-gradient(90deg, ${colors.join(',')})` }} />;
  return (
    <span aria-hidden style={{ ...common, display: 'flex' }}>
      {colors.map((c, i) => <span key={i} style={{ flex: 1, background: c }} />)}
    </span>
  );
}

export default function LeftRail(props: {
  lens: LensId; setLens: (l: LensId) => void;
  splits: LensId[]; setSplits: (s: LensId[]) => void;
  colOrder: string[]; setColOrder: (k: string[]) => void;   // units-grid column order — saved with the view
  query: string; setQuery: (q: string) => void; onSearchSubmit: () => void;
  parcels: Parcel[]; visible: Parcel[];
  filters: FilterState; setFilters: (f: FilterState) => void;
  onSelectRow: (id: string) => void;
  fieldNames: Record<string, string>;
  missingFields?: { id: string; label: string }[];
}) {
  const { lens, setLens, splits, setSplits, colOrder, setColOrder, query, setQuery, onSearchSubmit, parcels, visible, filters, setFilters, onSelectRow, fieldNames, missingFields } = props;
  const [missingDismissed, setMissingDismissed] = useState(false);
  const missingSet = useMemo(() => new Set((missingFields || []).map(f => f.id)), [missingFields]);
  // Color By disclosure state — open the group that holds the current lens on first render; the user
  // toggles freely after. (Initialized once; not re-synced on external lens changes, so a manual
  // collapse is never overridden.)
  const groupOf = (id: LensId) => LENS_GROUPS.find(g => g.ids.includes(id))?.label || LENS_GROUPS[0].label;
  // Single-open accordion: opening a group collapses the others, so the 24-lens list never sprawls.
  const [openGroup, setOpenGroup] = useState<string>(() => groupOf(lens));
  const toggleGroup = (label: string) => setOpenGroup(prev => (prev === label ? '' : label));
  // Lens filter — narrows the Color-by list by lens NAME (the coloring options, not category values);
  // a match force-opens its group so it's never hidden behind a collapsed header.
  const [lensQuery, setLensQuery] = useState('');
  // Picking a lens opens its group and clears the filter, so after a search you land back in the
  // normal accordion with the chosen lens's group open (not stranded behind a stale query).
  const pickLens = (id: LensId) => { setLens(id); setOpenGroup(groupOf(id)); setLensQuery(''); };
  // The whole Color-by section is collapsible (like the filter sections); collapsed, its header
  // shows the active lens so you still know what the map is coloured by.
  const [colorByOpen, setColorByOpen] = useState(true);
  const [name, setName] = useState('');
  const gc = useGlobalConfig();
  const base = useBase();
  const canWrite = gc.hasPermissionToSet('lfeSavedViews');
  const shared = (gc.get('lfeSavedViews') as any[]) || [];
  // Namespaced personal-views key; one-time copy off the legacy un-namespaced key happens
  // BEFORE the first read (the memo runs during the initial render, ahead of the lazy
  // useState initializer below), so existing saved views survive the rename.
  const lsKey = useMemo(() => {
    const k = railViewsKey(base.id, getBlockIdBestEffort());
    try { migrateKeyKeepLegacy(localStorage, LEGACY_LS_KEY, k); } catch { /* sandboxed */ }
    return k;
  }, [base.id]);
  const [personal, setPersonal] = useState<any[]>(() => lsRead(lsKey));
  // Shared (base-level) views first, then personal ones not shadowed by a shared name.
  const saved = [
    ...shared.map(v => ({ ...v, scope: 'shared' as const })),
    ...personal.filter(p => !shared.some(s => s.name === p.name)).map(v => ({ ...v, scope: 'mine' as const })),
  ];
  const fc = filterCount(filters);

  // Multi-result search across every advertised criterion: lot, unit, buyer, model, phase, status.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase(); if (!q) return [] as Parcel[];
    return parcels.filter(p => searchHaystack(p).includes(q)).slice(0, 8);
  }, [query, parcels]);

  const saveAs = async (nm: string) => {
    const entry = { name: nm, lens, f: serFilters(filters), s: splits, co: colOrder };  // s: split layers, co: units-grid column order
    if (canWrite) {                                   // base editor → shared, visible to everyone
      await gc.setAsync('lfeSavedViews', [...shared.filter(v => v.name !== nm), entry]);
    } else {                                           // viewer → personal, this browser
      const next = [...personal.filter(v => v.name !== nm), entry]; setPersonal(next); lsWrite(lsKey, next);
    }
  };
  const saveView = async () => {
    const nm = name.trim(); if (!nm) return;
    await saveAs(nm);
    setName('');
  };
  /** Overwrite an existing view with the CURRENT map state (lens + filters + splits). */
  const updateView = async (v: any) => { await saveAs(v.name); };
  /** Promote a personal (browser-local) view to a base-wide SHARED one using the view's OWN stored
   *  config — NOT the current map state, so it works without applying the view first (no footgun).
   *  Needs publish permission (editor in the interface builder; globalConfig is read-only in the
   *  published runtime). The personal copy stays (now shadowed by the shared one) and reappears if the
   *  shared view is ever deleted. */
  const promoteToShared = async (v: any) => {
    if (!canWrite) return;
    const entry = { name: v.name, lens: v.lens, f: v.f, s: v.s, co: v.co };
    await gc.setAsync('lfeSavedViews', [...shared.filter(s => s.name !== v.name), entry]);
  };
  const nameExists = saved.some(v => v.name === name.trim());
  // Guard the lens: a saved view may carry a lens id that was since renamed/removed —
  // setting it unguarded would make downstream LENSES[lens] lookups throw.
  const applyView = (v: any) => {
    const ln = (v.lens && LENSES[v.lens as LensId]) ? v.lens : 'landUse';
    setLens(ln); setFilters(deserFilters(v.f));
    if (Array.isArray(v.s)) setSplits(v.s.filter((id: any) => LENSES[id as LensId] && id !== ln));
    setColOrder(Array.isArray(v.co) ? v.co.filter((k: any) => typeof k === 'string') : []);
  };
  const delView = async (v: any) => {
    if (v.scope === 'shared') { if (canWrite) await gc.setAsync('lfeSavedViews', shared.filter(s => s.name !== v.name)); }
    else { const next = personal.filter(s => s.name !== v.name); setPersonal(next); lsWrite(lsKey, next); }
    if (defaultName === v.name) setDefault(v); // deleting the default view clears the pointer too
  };

  // ── Default view ──────────────────────────────────────────────────────────────────────────────
  // The view that auto-applies on load. Personal pointer wins over the shared (base-wide) one, so an
  // individual can override the team default in their own browser without touching it for everyone.
  const defaultLsKey = lsKey + '::default';
  const [personalDefault, setPersonalDefault] = useState<string>(() => lsReadStr(defaultLsKey));
  const sharedDefault = (gc.get(DEFAULT_GC_KEY) as string) || '';
  const defaultName = personalDefault || sharedDefault;
  const isDefault = (v: any) => v.name === defaultName;
  // Toggle a view as the default. Editors set the SHARED default off a shared view; everyone can set
  // a PERSONAL default. Clicking the current default again clears it.
  const setDefault = async (v: any) => {
    if (v.scope === 'shared' && canWrite) {
      const next = sharedDefault === v.name ? undefined : v.name;
      await gc.setAsync(DEFAULT_GC_KEY, next);
      // Setting a base-wide default clears this editor's personal override so the shared default
      // actually takes effect in their own browser too (personal otherwise silently wins). Unsetting
      // leaves their personal pointer alone.
      if (next && personalDefault) { setPersonalDefault(''); lsWriteStr(defaultLsKey, ''); }
    } else {
      const next = personalDefault === v.name ? '' : v.name;
      setPersonalDefault(next); lsWriteStr(defaultLsKey, next);
    }
  };
  // Apply the default view ONCE per page load (empty deps → fires on first mount, before any
  // interaction). Guarded so a user's own navigation later in the session is never clobbered.
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    appliedDefaultRef.current = true;
    if (!defaultName) return;
    const v = saved.find(s => s.name === defaultName);
    if (v) applyView(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.paper, borderRight: `1px solid ${T.line}` }}>
      <div style={{ background: T.maroon, color: '#fff', padding: '12px 18px' }}>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '.16em', opacity: .85 }}>LAKEFRONT ESTATES</div>
        <div className="head" style={{ fontSize: 19, color: '#fff', marginTop: 2 }}>Site Map</div>
      </div>

      <div className="lfe-scroll" style={{ overflow: 'auto', flex: 1, padding: '14px 18px' }}>
        {/* Trust guard: a Color-By/filter field the configured data source doesn't expose loads
            EMPTY with no other signal — so say so explicitly instead of a silent blank. */}
        {missingFields && missingFields.length > 0 && !missingDismissed && (
          <div role="status" style={{ marginBottom: 12, padding: '8px 10px', background: T.amberTint, border: `1px solid ${T.amber}`, borderRadius: 8, fontSize: 11, color: T.ink, lineHeight: 1.4 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>⚠ {missingFields.length} field{missingFields.length > 1 ? 's' : ''} not in this element’s data source</strong> — {missingFields.map(f => f.label).join(', ')}. They read empty until added to the Site Map data source in the interface designer.
              </span>
              <button onClick={() => setMissingDismissed(true)} aria-label="Dismiss data-source warning" title="Dismiss" className="lfe-press"
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.inkSoft, fontSize: 12, lineHeight: 1, padding: 0, flex: '0 0 auto' }}>✕</button>
            </div>
          </div>
        )}
        {/* Find a parcel — moved to the TOP of the rail (above Color by) per owner request. The
            filter-count + Clear all live here too, as the rail's global filter reset. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 6px' }}>
          <span className="eyebrow">Find a parcel</span>
          {fc > 0 && <button onClick={() => setFilters(emptyFilters())} className="lfe-press"
            title="Clear every active filter"
            style={{ fontSize: 11, fontWeight: 600, color: T.onAccent, background: T.accent, border: 'none', borderRadius: 999, padding: '2px 10px', cursor: 'pointer' }}>
            {fc} filter{fc > 1 ? 's' : ''} · Clear all</button>}
        </div>
        <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { if (matches[0]) { onSelectRow(matches[0].id); setQuery(''); } else onSearchSubmit(); } }}
          placeholder="Lot, address, buyer, model, phase, status…"
          style={{ width: '100%', padding: '9px 11px', border: `1px solid ${T.line}`, borderRadius: 9, background: T.surface, fontSize: 13 }} />
        {query.trim() && (
          <div style={{ marginTop: 4, border: `1px solid ${T.line}`, borderRadius: 9, overflow: 'hidden', background: T.surface, boxShadow: T.shadow }}>
            {matches.length === 0
              ? <div style={{ padding: '8px 11px', fontSize: 12, color: T.inkSoft }}>No matches for “{query.trim()}”.</div>
              : matches.map(p => {
                // Two-line result: lot # + street address as identity, secondary context muted below.
                const addrShort = p.address ? p.address.split(',')[0].trim() : '';
                const sub = [p.unit?.buyer || p.buyersAll, p.model, p.geo.phase].filter(Boolean).join(' · ');
                return (
                  <button key={p.id} onClick={() => { onSelectRow(p.id); setQuery(''); }} className="lfe-press"
                    style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', textAlign: 'left', padding: '7px 11px', border: 'none', borderBottom: `1px solid ${T.sand}`, background: T.surface, cursor: 'pointer' }}>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, width: '100%' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, flex: '0 0 auto' }}>{p.name}</span>
                      {addrShort && <span className="num" title={p.address} style={{ marginLeft: 'auto', fontSize: 11.5, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '72%' }}>{addrShort}</span>}
                    </span>
                    {sub && <span style={{ fontSize: 11, color: T.inkSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{sub}</span>}
                  </button>
                );
              })}
          </div>
        )}

        {/* Color by — collapsible section header (mirrors the filter sections). Collapsed, it shows
            the active lens on the right so you still know the map's colouring. */}
        <button onClick={() => setColorByOpen(o => !o)} className="lfe-press" aria-expanded={colorByOpen}
          style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '14px 0 6px' }}>
          <span style={{ fontSize: 10, color: T.inkSoft, transform: colorByOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s', width: 12, textAlign: 'center', flex: '0 0 auto', pointerEvents: 'none' }}>▶</span>
          <span className="eyebrow" style={{ margin: 0, pointerEvents: 'none' }}>Color by</span>
          {!colorByOpen && (
            <span className="num" style={{ marginLeft: 'auto', fontSize: 11, color: T.accent, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '58%', pointerEvents: 'none' }}>
              {lensLabel(LENSES[lens], fieldNames)}
            </span>
          )}
        </button>
        {colorByOpen && (<>
        {/* Lens filter — narrows the list by lens NAME (the coloring options), NOT category values;
            value search lives in "Find a parcel" below. A match force-opens its group. */}
        <div style={{ position: 'relative', marginBottom: 6 }}>
          <span aria-hidden style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: T.inkSoft, pointerEvents: 'none' }}>⌕</span>
          <input value={lensQuery} onChange={e => setLensQuery(e.target.value)} aria-label="Filter colour-by lenses by name"
            placeholder="Filter lenses…" spellCheck={false}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 26px', fontSize: 12, border: `1px solid ${T.line}`,
              borderRadius: 8, background: T.surface, color: T.ink, outline: 'none' }} />
          {lensQuery && (
            <button onClick={() => setLensQuery('')} aria-label="Clear lens filter" className="lfe-press"
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, borderRadius: '50%',
                border: 'none', cursor: 'pointer', background: T.sand2, color: T.inkSoft, fontSize: 10, lineHeight: 1, display: 'grid', placeItems: 'center', padding: 0 }}>✕</button>
          )}
        </div>
        {/* Collapsible group accordion (single-open). When the lens filter is active, groups are
            filtered to matching lenses and force-open; a collapsed group shows the active lens. */}
        {(() => {
          const q = lensQuery.trim().toLowerCase();
          const labelOf = (id: LensId) => lensLabel(LENSES[id], fieldNames);
          const groups = LENS_GROUPS
            .map(g => ({ label: g.label, ids: q ? g.ids.filter(id => labelOf(id).toLowerCase().includes(q)) : g.ids }))
            .filter(g => g.ids.length > 0);
          if (!groups.length) return (
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 9, background: T.surface, padding: '14px 12px', fontSize: 12.5, color: T.inkSoft, textAlign: 'center' }}>
              No lenses match “{lensQuery.trim()}”
            </div>
          );
          return (
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 9, background: T.surface, overflow: 'hidden' }}>
          {groups.map((g, gi) => {
            const open = q ? true : openGroup === g.label;
            const activeHere = g.ids.includes(lens);
            return (
              <div key={g.label} style={{ borderTop: gi === 0 ? 'none' : `1px solid ${T.sand}` }}>
                <button onClick={() => { if (!q) toggleGroup(g.label); }} className="lfe-press"
                  aria-expanded={open}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                    // Open group wins the sand2 band; collapsed headers zebra (alternate sand) so the
                    // group list reads as distinct rows at a glance.
                    background: open ? T.sand2 : (gi % 2 ? T.sand : 'transparent'), border: 'none', cursor: q ? 'default' : 'pointer', padding: '8px 10px' }}>
                  <span style={{ fontSize: 10, color: T.inkSoft, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', width: 12, textAlign: 'center', flex: '0 0 auto', pointerEvents: 'none' }}>▶</span>
                  <span className="eyebrow" style={{ margin: 0, pointerEvents: 'none' }}>{g.label}</span>
                  {!open && activeHere && (
                    <span className="num" style={{ marginLeft: 'auto', fontSize: 11, color: T.accent, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '58%', pointerEvents: 'none' }}>
                      {lensLabel(LENSES[lens], fieldNames)}
                    </span>
                  )}
                </button>
                {open && (
                  <div style={{ paddingBottom: 4 }}>
                    {g.ids.map((id, ri) => {
                      const fid = LENSES[id].fieldId; const miss = !!(fid && missingSet.has(fid));
                      const on = id === lens;
                      // Swatch left of each label previews the lens's palette (the picker's own answer);
                      // a faint zebra (odd rows = sand) plus a sand2 hover (CSS .lens-row) give rhythm +
                      // feedback. Selected row keeps the full accent band, which wins over the zebra.
                      return (
                        <button key={id} onClick={() => pickLens(id)} className={'lfe-press lens-row' + (on ? ' on' : '')}
                          aria-pressed={on}
                          style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                            border: 'none', cursor: 'pointer', padding: '6px 10px 6px 12px', fontSize: 12.5,
                            fontWeight: on ? 600 : 400, color: on ? T.onAccent : (miss ? T.amber : T.ink),
                            background: on ? T.accent : (ri % 2 ? T.sand : 'transparent') }}>
                          <LensSwatch lens={LENSES[id]} on={on} />
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {miss ? '⚠ ' : ''}{lensLabel(LENSES[id], fieldNames)}{miss ? ' · no data source' : ''}
                          </span>
                          {on && <span style={{ flex: '0 0 auto', fontSize: 11 }}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
          );
        })()}
        </>)}

        {/* Split by — the SAME branded checkbox-list picker as the analytics panel (one synced
            state lifted in SiteMapApp; the map hover tooltip reads it too). Hidden for buildOrder
            (the heat ramp has no categorical rows to split). */}
        {lens !== 'buildOrder' && (
          <>
            <div className="eyebrow" style={{ margin: '10px 0 6px' }}>Split by</div>
            <SplitPicker lens={lens} splits={splits} setSplits={setSplits} fieldNames={fieldNames} block
              triggerStyle={{ padding: '8px 10px', fontSize: 12.5, borderRadius: 9 }} />
          </>
        )}

        <Filters parcels={parcels} filters={filters} setFilters={setFilters} fieldNames={fieldNames} />

        {/* Record tabs (Permits/Inspections/Sales/Units) now live in the bottom dock console.
            The rail keeps Color By, Search, Filters, and this compact Saved-views shortcut. */}
        <div className="eyebrow" style={{ margin: '16px 0 8px' }}>Saved views</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Name this view…"
            style={{ flex: 1, minWidth: 0, padding: '7px 9px', border: `1px solid ${T.line}`, borderRadius: 7, background: T.surface, fontSize: 12.5 }} />
          <button onClick={saveView} disabled={!name.trim()} className="lfe-press" style={tabStyle(true)}
            title={nameExists ? 'A view with this name exists — overwrites it with the current state' : undefined}>{nameExists ? 'Update' : 'Save'}</button>
        </div>
        <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 6 }}>
          {canWrite
            ? 'Saved as shared views for everyone on this base. Tap ⤴ on a personal view to publish it.'
            : 'Saved to this browser (personal). To publish a view for everyone, open this interface in edit mode.'}
          {' '}Tap ☆ to make a view the default the map opens in.
        </div>
        {saved.length === 0 ? <div style={{ fontSize: 12.5, color: T.inkSoft, padding: '6px 0' }}>No saved views yet.</div> :
          saved.map(v => (
            <div key={v.scope + ':' + v.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px dashed ${T.line}` }}>
              <button onClick={() => setDefault(v)} className="lfe-press"
                title={isDefault(v)
                  ? 'This is the default view shown when the map opens — click to unset'
                  : (v.scope === 'shared' && canWrite ? 'Set as the base-wide default view shown when the map opens' : 'Set as your default view shown when the map opens')}
                aria-label={`${isDefault(v) ? 'Unset' : 'Set'} default view ${v.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDefault(v) ? T.gold : T.inkSoft, fontSize: 14, lineHeight: 1, flex: '0 0 auto', padding: 0 }}>{isDefault(v) ? '★' : '☆'}</button>
              <button onClick={() => applyView(v)} className="lfe-press" style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: T.ink }}>
                {v.name} <span style={{ color: T.inkSoft }}>· {LENSES[v.lens as LensId]?.label || v.lens}</span>
                {isDefault(v) && <span style={{ marginLeft: 5, fontSize: 10, color: T.gold, border: `1px solid ${T.gold}`, borderRadius: 4, padding: '0 4px', verticalAlign: 'middle' }}>DEFAULT</span>}
                {v.scope === 'shared' && <span style={{ marginLeft: 5, fontSize: 10, color: T.accent, border: `1px solid ${T.accent}`, borderRadius: 4, padding: '0 4px', verticalAlign: 'middle' }}>SHARED</span>}
              </button>
              {v.scope === 'mine' && canWrite && <button onClick={() => promoteToShared(v)} className="lfe-press"
                title={`Publish “${v.name}” as a shared view for everyone on this base`} aria-label={`Make view ${v.name} shared`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontSize: 14 }}>⤴</button>}
              {(v.scope === 'mine' || canWrite) && <button onClick={() => updateView(v)} className="lfe-press"
                title={`Overwrite “${v.name}” with the current lens, filters, and splits`} aria-label={`Update view ${v.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontSize: 13 }}>↻</button>}
              {(v.scope === 'mine' || canWrite) && <button onClick={() => delView(v)} className="lfe-press" title={`Delete “${v.name}”`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.inkSoft, fontSize: 13 }}>✕</button>}
            </div>
          ))}
      </div>

      <div style={{ padding: '9px 18px', borderTop: `1px solid ${T.line}`, fontSize: 11.5, color: T.inkSoft }} className="num">
        {fmt(visible.length)} of {fmt(parcels.length)} lots in view
      </div>
    </div>
  );
}

const tabStyle = (on: boolean): React.CSSProperties => ({ padding: '5px 8px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', borderRadius: 7, border: `1px solid ${on ? T.ink : T.line}`, background: on ? T.ink : T.surface, color: on ? T.onInk : T.inkSoft });
